import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Typography, Table, Select, DatePicker, Segmented, Spin, Alert, Space, Button, Tooltip,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import CoachingDueDatesTab from '../components/CoachingDueDatesTab';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Tabs share one endpoint; each is a filter over the same shape.
// `filters` lists which controls apply. Request Type is not offered on the
// recoaching tab: it reads AgentMonthlyStats.RecoachingCount, which is where
// live sessions and the backfilled referrals meet, and that column carries no
// request type. Showing the control would imply a filter that does nothing.
// Coaching went live on this date. Sessions before it are test data, and the
// referrals backfilled into AgentMonthlyStats for earlier months were never
// raised as sessions -- so a By Request Type range that reaches back further
// just shows empty months and invites the question of where the data went.
const COACHING_GO_LIVE = '2026-09-01';

const TABS = [
  { label: 'Recoachings (Referral)', value: 'recoaching',
    filters: ['range', 'campaign'],
    endpoint: '/api/coaching/agent-stats',
    // Row identity and the label shown in the second column.
    rowKeyOf: r => `${r.Campaign}||${r.UserID}`,
    dimKey: 'AgentName', dimTitle: 'Agent', dimWidth: 190 },
  { label: 'By Request Type', value: 'request_type',
    // Counted off CoachingSessions, which does carry a request type, so the
    // filter is offered here where it does nothing on the recoaching tab.
    filters: ['range', 'campaign', 'type'],
    endpoint: '/api/coaching/agent-stats/by-request-type',
    rowKeyOf: r => `${r.Campaign}||${r.RequestType}`,
    dimKey: 'RequestType', dimTitle: 'Request Type', dimWidth: 200,
    minFrom: COACHING_GO_LIVE,
    // Referrals are all but one-to-one session-to-participant, so the second
    // figure only earns its place here.
    hasParticipants: true },
  // Its own list and summary (CoachingDueDatesTab), not the month pivot; the
  // range is the due date.
  { label: 'Due Dates', value: 'due_dates',
    filters: ['range', 'campaign', 'type'],
    custom: true,
    minFrom: COACHING_GO_LIVE },
];

// A cell carrying both figures: sessions as the headline, participants under
// it in muted text. The top line is a bare number, so the tooltip is what says
// it is a session count. Tabs without participants render the single number.
function Cell({ sessions, people, showPeople, strong }) {
  if (!sessions && !people) return <span style={{ color: '#d9d9d9' }}>-</span>;
  const top = strong ? <strong>{sessions || 0}</strong> : (sessions || 0);
  if (!showPeople) return top;
  const s = sessions || 0;
  const p = people || 0;
  return (
    <Tooltip title={`${s} session${s === 1 ? '' : 's'}, ${p} participant${p === 1 ? '' : 's'}`}>
      <div style={{ lineHeight: 1.25, cursor: 'default' }}>
        <div>{top}</div>
        <div style={{ fontSize: 11, color: '#8c8c8c' }}>{p} participant{p === 1 ? '' : 's'}</div>
      </div>
    </Tooltip>
  );
}

function monthLabel(mk) {
  if (!mk) return mk;
  return dayjs(`${mk}-01`).format("MMM 'YY");
}

/**
 * `embedded` renders this page's content without its own heading or filter
 * controls, scoped by the campaign and window passed in. The Campaign Summary
 * uses it so there is one implementation of these numbers rather than two that
 * can drift apart.
 */
export default function CoachingAgentStatsPage({ embedded = false, campaignId = null, from: fromProp = null, to: toProp = null }) {
  const currentUser = useContext(UserContext);
  const canView = currentUser?.Role !== 'agent' && (currentUser?.CanEditCoaching ?? 0) >= 1;

  const [tab, setTab]         = useState('recoaching');
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ request_types: [], campaigns: [] });

  const [range, setRange]       = useState(
    embedded && fromProp && toProp ? [dayjs(fromProp), dayjs(toProp)] : [dayjs().startOf('year'), dayjs()]
  );
  const [typeIds, setTypeIds]   = useState([]);
  const [campaignIds, setCampIds] = useState(embedded && campaignId ? [campaignId] : []);

  // Embedded: follow the page above when it changes campaign or window.
  useEffect(() => {
    if (!embedded) return;
    setCampIds(campaignId ? [campaignId] : []);
    if (fromProp && toProp) setRange([dayjs(fromProp), dayjs(toProp)]);
  }, [embedded, campaignId, fromProp, toProp]);

  const from = range?.[0]?.format('YYYY-MM-DD');
  const to   = range?.[1]?.format('YYYY-MM-DD');
  const activeTab = TABS.find(t => t.value === tab) ?? TABS[0];

  // Switching to a tab with a floor pulls the range up to it rather than
  // silently returning nothing for the months before it.
  useEffect(() => {
    if (!activeTab.minFrom) return;
    const floor = dayjs(activeTab.minFrom);
    setRange(([a, b]) => {
      if (a && !a.isBefore(floor, 'day')) return [a, b];
      const start = floor;
      const end   = b && b.isBefore(start, 'day') ? dayjs() : b;
      return [start, end];
    });
  }, [activeTab]);

  useEffect(() => {
    if (!canView) return;
    api.get('/api/coaching/agent-stats/filters')
      .then(r => setFilters(r.data || { request_types: [], campaigns: [] }))
      .catch(() => {});
  }, [canView]);

  useEffect(() => {
    if (!canView || !from || !to) return;
    const t = TABS.find(x => x.value === tab) ?? TABS[0];
    if (t.custom) { setRows([]); setLoading(false); return; }   // the tab loads its own data
    setLoading(true);
    setRows([]);
    api.get(t.endpoint, {
      params: {
        date_from: from,
        date_to:   to,
        ...(campaignIds.length ? { campaign_id: campaignIds.join(',') } : {}),
        ...(t.filters.includes('type') && typeIds.length
          ? { request_type_id: typeIds.join(',') } : {}),
      },
    })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [canView, tab, from, to, campaignIds, typeIds]);

  // Pivot: one row per campaign + the active tab's dimension (agent, or request
  // type), one column per month present.
  // Sessions live on the row under the month key itself so the column sorters
  // keep working on a plain number; participants ride alongside in `P`.
  const { months, pivot, totalsByMonth, totalsByMonthP, grandTotal, grandTotalP } = useMemo(() => {
    // A custom tab (Due Dates) has no pivot; the previous tab's rows can still
    // be here for a render after switching to it.
    if (activeTab.custom) {
      return { months: [], pivot: [], totalsByMonth: {}, totalsByMonthP: {}, grandTotal: 0, grandTotalP: 0 };
    }
    const mSet = [...new Set(rows.map(r => r.MonthKey))].sort();
    const byKey = {};
    const mTot  = {};
    const mTotP = {};
    let grand = 0;
    let grandP = 0;
    rows.forEach(r => {
      const key = activeTab.rowKeyOf(r);
      if (!byKey[key]) {
        byKey[key] = { _key: key, Campaign: r.Campaign, Dim: r[activeTab.dimKey],
                       Total: 0, TotalP: 0, P: {} };
      }
      const ppl = r.Participants || 0;
      byKey[key][r.MonthKey]  = (byKey[key][r.MonthKey] || 0) + r.Sessions;
      byKey[key].P[r.MonthKey] = (byKey[key].P[r.MonthKey] || 0) + ppl;
      byKey[key].Total       += r.Sessions;
      byKey[key].TotalP      += ppl;
      mTot[r.MonthKey]        = (mTot[r.MonthKey]  || 0) + r.Sessions;
      mTotP[r.MonthKey]       = (mTotP[r.MonthKey] || 0) + ppl;
      grand                  += r.Sessions;
      grandP                 += ppl;
    });
    return { months: mSet, pivot: Object.values(byKey),
             totalsByMonth: mTot, totalsByMonthP: mTotP,
             grandTotal: grand, grandTotalP: grandP };
  }, [rows, activeTab]);

  // Sorting is controlled here rather than left to the Table's own sorters so
  // that Campaign always leads and the chosen sort applies within each campaign.
  // The table is a pivot by campaign and agent, so keeping same-campaign rows
  // adjacent is what makes it readable.
  const [sortBy, setSortBy] = useState({ field: 'Total', order: 'descend' });

  const displayRows = useMemo(() => {
    const dir = sortBy.order === 'ascend' ? 1 : -1;
    const within = (a, b) => {
      if (!sortBy.field || !sortBy.order) return 0;
      if (sortBy.field === 'Dim') {
        return dir * (a.Dim || '').localeCompare(b.Dim || '');
      }
      return dir * ((a[sortBy.field] || 0) - (b[sortBy.field] || 0));
    };
    return [...pivot].sort((a, b) =>
      (a.Campaign || '').localeCompare(b.Campaign || '')
      || within(a, b)
      || (a.Dim || '').localeCompare(b.Dim || ''));
  }, [pivot, sortBy]);

  const handleTableChange = (_pagination, _filters, s) => {
    setSortBy(s?.order ? { field: s.columnKey, order: s.order } : { field: null, order: null });
  };

  // Exports the rows as shown -- same filters, same order, one column per month
  // present, so the sheet matches what is on screen rather than the raw feed.
  const exportToExcel = () => {
    // With two figures per cell the sheet gets a Metric column and two rows per
    // group, rather than doubling the month columns -- that stays pivotable.
    const withPeople = activeTab.hasParticipants;
    const line = (label, dim, get) => {
      const row = { Campaign: label, [activeTab.dimTitle]: dim };
      if (withPeople) row.Metric = get.metric;
      months.forEach(mk => { row[monthLabel(mk)] = get.month(mk); });
      row.Total = get.total;
      return row;
    };
    const sheet = [];
    displayRows.forEach(r => {
      sheet.push(line(r.Campaign, r.Dim,
        { metric: 'Sessions', month: mk => r[mk] ?? 0, total: r.Total }));
      if (withPeople) {
        sheet.push(line(r.Campaign, r.Dim,
          { metric: 'Participants', month: mk => r.P?.[mk] ?? 0, total: r.TotalP }));
      }
    });
    sheet.push(line('Total', '',
      { metric: 'Sessions', month: mk => totalsByMonth[mk] || 0, total: grandTotal }));
    if (withPeople) {
      sheet.push(line('Total', '',
        { metric: 'Participants', month: mk => totalsByMonthP[mk] || 0, total: grandTotalP }));
    }

    const ws = XLSX.utils.json_to_sheet(sheet);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab === 'recoaching' ? 'Recoachings' : 'By Request Type');
    XLSX.writeFile(wb, `coaching-${tab.replace('_', '-')}-${from}-to-${to}.xlsx`);
  };

  if (!canView) {
    return <div style={{ padding: 24 }}><Text type="secondary">Access denied.</Text></div>;
  }

  const columns = [
    {
      // Repeated on every row rather than merged into one spanning cell: the
      // list runs long enough that a merged label scrolls out of view and you
      // lose track of which campaign the person in front of you belongs to.
      title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 170, fixed: 'left',
      render: v => <Text strong>{v}</Text>,
    },
    { title: activeTab.dimTitle, dataIndex: 'Dim', key: 'Dim',
      width: activeTab.dimWidth, fixed: 'left',
      sorter: true, sortOrder: sortBy.field === 'Dim' ? sortBy.order : null },
    ...months.map(mk => ({
      title: monthLabel(mk), key: mk, width: activeTab.hasParticipants ? 120 : 90, align: 'right',
      sorter: true, sortOrder: sortBy.field === mk ? sortBy.order : null,
      render: (_, r) => (
        <Cell sessions={r[mk]} people={r.P?.[mk]} showPeople={activeTab.hasParticipants} />
      ),
    })),
    { title: 'Total', dataIndex: 'Total', key: 'Total',
      width: activeTab.hasParticipants ? 120 : 90, align: 'right',
      sorter: true, sortOrder: sortBy.field === 'Total' ? sortBy.order : null,
      render: (_, r) => (
        <Cell sessions={r.Total} people={r.TotalP} showPeople={activeTab.hasParticipants} strong />
      ) },
  ];

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && (
        <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>Coaching Agent Stats</Title>
      )}

      <Space wrap style={{ marginBottom: 12 }}>
        {/* Guarded one by one, not wrapped in a fragment: Space spaces its
            direct children, so a fragment would collapse these two into one
            and lose the gap between them. A false child is dropped. */}
        {!embedded && <Text type="secondary">{tab === 'due_dates' ? 'Due Date' : 'Months'}</Text>}
        {!embedded && (
        <RangePicker
          value={range}
          onChange={setRange}
          allowClear={false}
          disabledDate={d => !!activeTab.minFrom && !!d && d.isBefore(dayjs(activeTab.minFrom), 'day')}
        />
        )}
        {activeTab.filters.includes('type') && (
          <Select
            mode="multiple"
            allowClear showSearch optionFilterProp="label"
            maxTagCount="responsive"
            style={{ minWidth: 220, maxWidth: 420 }} placeholder="All request types"
            value={typeIds} onChange={setTypeIds}
            options={filters.request_types.map(t => ({
              label: t.IsRecoaching ? `${t.Name} (recoaching)` : t.Name, value: t.ID,
            }))}
          />
        )}
        {!embedded && (
        <Select
          mode="multiple"
          allowClear showSearch optionFilterProp="label"
          maxTagCount="responsive"
          style={{ minWidth: 220, maxWidth: 420 }} placeholder="All campaigns"
          value={campaignIds} onChange={setCampIds}
          options={filters.campaigns.map(c => ({ label: c.Name, value: c.ID }))}
        />
        )}
        <Segmented
          options={TABS.map(t => ({ label: t.label, value: t.value }))}
          value={tab}
          onChange={setTab}
        />
      </Space>

      {activeTab.custom ? (
        <CoachingDueDatesTab from={from} to={to} campaignIds={campaignIds} typeIds={typeIds} />
      ) : loading ? <Spin /> : pivot.length === 0 ? (
        <Alert type="info" showIcon message={
          tab === 'recoaching'
            ? 'No recoachings match these filters.'
            : 'No coaching sessions match these filters.'
        } />
      ) : (
        <>
          <Space style={{ margin: '12px 0 8px 8px' }}>
            <Button size="small" icon={<DownloadOutlined />} onClick={exportToExcel}>
              Export to Excel
            </Button>
          </Space>
          <Table
            rowKey="_key"
            dataSource={displayRows}
            columns={columns}
            onChange={handleTableChange}
            size="small"
            bordered
            pagination={false}
            sticky
            scroll={{ x: 'max-content' }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
                  {months.map((mk, i) => (
                    <Table.Summary.Cell key={mk} index={i + 2} align="right">
                      <Cell sessions={totalsByMonth[mk]} people={totalsByMonthP[mk]}
                            showPeople={activeTab.hasParticipants} strong />
                    </Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={months.length + 2} align="right">
                    <Cell sessions={grandTotal} people={grandTotalP}
                          showPeople={activeTab.hasParticipants} strong />
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </>
      )}
    </div>
  );
}
